import { useAccount, useReadContracts, useWriteContract, useWaitForTransactionReceipt } from "wagmi";
import { useEffect } from "react";
import type { Address } from "viem";
import { maxUint256 } from "viem";
import { PIGGY_TOKEN_ABI, PIGGY_STAKING_ABI } from "@/lib/abis";
import { ADDRESSES } from "@/lib/config";

const ZERO: Address = "0x0000000000000000000000000000000000000000";

export interface StakingPosition {
    walletBalance: bigint;
    allowance: bigint;
    stakedAmount: bigint;
    earned: bigint;
    totalStaked: bigint;
    totalReceived: bigint;
}

const DEFAULTS: StakingPosition = {
    walletBalance: 0n,
    allowance: 0n,
    stakedAmount: 0n,
    earned: 0n,
    totalStaked: 0n,
    totalReceived: 0n,
};

export function useStakingPosition(): { pos: StakingPosition; live: boolean; refetch: () => void } {
    const { address } = useAccount();
    const token = ADDRESSES.PIGGY_TOKEN;
    const staking = ADDRESSES.PIGGY_STAKING;
    const user: Address = address ?? ZERO;

    const reads = useReadContracts({
        allowFailure: true,
        contracts: token && staking
            ? [
                  { address: token,   abi: PIGGY_TOKEN_ABI,   functionName: "balanceOf",   args: [user] } as const,
                  { address: token,   abi: PIGGY_TOKEN_ABI,   functionName: "allowance",   args: [user, staking] } as const,
                  { address: staking, abi: PIGGY_STAKING_ABI, functionName: "userStateOf", args: [user] } as const,
                  { address: staking, abi: PIGGY_STAKING_ABI, functionName: "earned",      args: [user] } as const,
                  { address: staking, abi: PIGGY_STAKING_ABI, functionName: "totalStaked"  } as const,
                  { address: staking, abi: PIGGY_STAKING_ABI, functionName: "totalReceived" } as const,
              ]
            : undefined,
        query: { enabled: Boolean(token && staking && address), refetchInterval: 15_000 },
    });

    if (!token || !staking || !address || !reads.data) {
        return { pos: DEFAULTS, live: false, refetch: () => reads.refetch() };
    }

    const state = reads.data[2]?.result as { stakedAmount: bigint } | undefined;

    return {
        pos: {
            walletBalance: (reads.data[0]?.result as bigint | undefined) ?? 0n,
            allowance:     (reads.data[1]?.result as bigint | undefined) ?? 0n,
            stakedAmount:  state?.stakedAmount ?? 0n,
            earned:        (reads.data[3]?.result as bigint | undefined) ?? 0n,
            totalStaked:   (reads.data[4]?.result as bigint | undefined) ?? 0n,
            totalReceived: (reads.data[5]?.result as bigint | undefined) ?? 0n,
        },
        live: true,
        refetch: () => reads.refetch(),
    };
}

function useTxStatus() {
    const { writeContract, data: hash, isPending, reset } = useWriteContract();
    const { isLoading: confirming, isSuccess: confirmed } = useWaitForTransactionReceipt({ hash });

    useEffect(() => {
        if (confirmed) {
            const t = setTimeout(reset, 4_000);
            return () => clearTimeout(t);
        }
    }, [confirmed, reset]);

    const status = confirmed
        ? ("confirmed" as const)
        : confirming
        ? ("confirming" as const)
        : isPending
        ? ("pending" as const)
        : ("idle" as const);

    return { writeContract, hash, status };
}

export function useApprovePiggy() {
    const token = ADDRESSES.PIGGY_TOKEN;
    const staking = ADDRESSES.PIGGY_STAKING;
    const tx = useTxStatus();

    const approve = () => {
        if (!token || !staking) return;
        tx.writeContract({
            address: token,
            abi: PIGGY_TOKEN_ABI,
            functionName: "approve",
            args: [staking, maxUint256],
        });
    };
    return { approve, ...tx };
}

export function useStakePiggy(amount: bigint) {
    const staking = ADDRESSES.PIGGY_STAKING;
    const tx = useTxStatus();

    const stake = () => {
        if (!staking || amount === 0n) return;
        tx.writeContract({
            address: staking,
            abi: PIGGY_STAKING_ABI,
            functionName: "stake",
            args: [amount],
        });
    };
    return { stake, ...tx };
}

export function useUnstakePiggy(amount: bigint) {
    const staking = ADDRESSES.PIGGY_STAKING;
    const tx = useTxStatus();

    const unstake = () => {
        if (!staking || amount === 0n) return;
        tx.writeContract({
            address: staking,
            abi: PIGGY_STAKING_ABI,
            functionName: "unstake",
            args: [amount],
        });
    };
    return { unstake, ...tx };
}

export function useClaimPiggyStaking() {
    const staking = ADDRESSES.PIGGY_STAKING;
    const tx = useTxStatus();

    const claim = () => {
        if (!staking) return;
        tx.writeContract({ address: staking, abi: PIGGY_STAKING_ABI, functionName: "claim", args: [] });
    };
    return { claim, ...tx };
}

export function useExitPiggyStaking() {
    const staking = ADDRESSES.PIGGY_STAKING;
    const tx = useTxStatus();

    const exit = () => {
        if (!staking) return;
        tx.writeContract({ address: staking, abi: PIGGY_STAKING_ABI, functionName: "exit", args: [] });
    };
    return { exit, ...tx };
}
